"use client";

import { Info, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Disclaimer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const policies = [{
    title: "Contact Requests",
    description: "When you submit a quote request, we use your information to contact you and review possible next steps."
  }, {
    title: "Information and Pricing",
    description: "Any estimate or quote information is preliminary until project details, materials, measurements, labor, and scope of work are reviewed and confirmed."
  }, {
    title: "Licensing and Insurance",
    description: "Some types of work may require licensed and insured professionals depending on the project scope. Customers should confirm licensing, insurance, and project terms before work begins."
  }, {
    title: "Project Timelines",
    description: "Project timelines can vary based on scope, material availability, scheduling, permits, site conditions, and other factors."
  }, {
    title: "Permits",
    description: "Permit requirements vary by project. Projects involving plumbing, electrical, structural changes, or major layout updates may require permits."
  }, {
    title: "Payment Terms",
    description: "Deposits, payment schedules, and final project costs should be reviewed and agreed to in writing before work begins."
  }];
  return <section data-section-id="797" id="website-disclaimer" className="bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8">
      <div ref={ref} className={`max-w-[1200px] mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="rounded-lg border border-border bg-card p-8 sm:p-12 lg:p-16 shadow-lg">
          {}
          <div className="flex items-center gap-3 mb-8">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-accent text-secondary">
              <Info className="w-5 h-5" strokeWidth={1.75} />
            </span>
            <span className="text-xs font-default font-semibold tracking-[0.2em] uppercase text-foreground">
              Important Notice
            </span>
          </div>

          {}
          <h2 className="font-serif font-default text-3xl sm:text-4xl lg:text-[2.5rem] leading-tight text-foreground max-w-3xl mb-12 lg:mb-16">Website Policies and Terms</h2>

          {}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-16">
            {policies.map((policy, i) => <div key={i} data-index={i} className={`flex gap-5 py-7 ${i < policies.length - 1 ? "border-b border-border" : ""} ${i === policies.length - 2 ? "md:border-b-0 border-b border-border" : ""}`}>
                {}
                <span className="font-serif italic text-secondary text-lg shrink-0 pt-1 min-w-[2.5rem]">
                  {String(i + 1).padStart(2, "0")}
                </span>

                {}
                <div className="flex-1">
                  <h3 className="font-serif font-default text-lg sm:text-xl text-foreground mb-2 leading-snug">
                    {policy.title}
                  </h3>
                  <p className="font-default text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {policy.description}
                  </p>
                </div>
              </div>)}
          </div>

          {}
          <div className="mt-12 lg:mt-16 rounded-lg bg-accent border border-border px-6 py-5 sm:px-8 sm:py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="font-serif italic text-base sm:text-lg text-foreground leading-relaxed">Have questions about our policies? Contact our team.</p>
            <Button asChild variant="link" className="text-foreground hover:text-secondary px-0 self-start sm:self-auto shrink-0 font-medium">
              <Link to="/Contact" className="inline-flex items-center gap-2">
                Contact our team
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}